import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { TEAM_LEADER_ROLE, TEAM_MEMBER_ROLE, type TeamRosterCell } from "_shared/entity-members/_views/team-roster-table";

/** Keeps a role select inside the Role column, which leaves under 80px inside its padding. */
const ROLE_SELECT_SX = { fontSize: "inherit", minWidth: 0 } as const;

/**
 * A member's Role cell as a compact select, so the leader can be changed where the role is read
 * rather than by removing people and adding them again (#6602). Choosing Leader asks the roster to
 * promote the member; the roster confirms first, because the leader it replaces becomes a member.
 *
 * Standard rather than outlined: the Role column leaves under 80px inside its padding, which is room
 * for "Member" and an arrow and not for a box around them.
 */
export function teamMemberRoleSelectCell(values: { name: string; onMakeLeader: () => void }): TeamRosterCell {
  return {
    searchText: TEAM_MEMBER_ROLE,
    sortText: TEAM_MEMBER_ROLE,
    content: (
      <Select<string>
        variant="standard"
        size="small"
        disableUnderline
        value={TEAM_MEMBER_ROLE}
        inputProps={{ "aria-label": `Role of ${values.name}` }}
        sx={ROLE_SELECT_SX}
        // A select only reports a choice that differs from its value, and a member's one other
        // choice is Leader.
        onChange={() => values.onMakeLeader()}
      >
        <MenuItem value={TEAM_LEADER_ROLE}>{TEAM_LEADER_ROLE}</MenuItem>
        <MenuItem value={TEAM_MEMBER_ROLE}>{TEAM_MEMBER_ROLE}</MenuItem>
      </Select>
    ),
  };
}

/**
 * The leader's Role as the same select a member's is, on a draft (#6861). Choosing Member asks the
 * roster to demote the leader, which leaves the team with no leader until somebody is chosen.
 *
 * Where the demotion cannot be made, Member stays in the list, disabled, with the reason under it: an
 * option that is just absent reads as a broken control.
 */
export function TeamLeaderRoleSelect(props: Readonly<{
  name: string;
  memberUnavailableReason?: string;
  onMakeMember: () => void;
}>) {
  return (
    <Select<string>
      variant="standard"
      size="small"
      disableUnderline
      value={TEAM_LEADER_ROLE}
      inputProps={{ "aria-label": `Role of ${props.name}` }}
      sx={ROLE_SELECT_SX}
      // The leader's one other choice is Member.
      onChange={() => props.onMakeMember()}
    >
      <MenuItem value={TEAM_LEADER_ROLE}>{TEAM_LEADER_ROLE}</MenuItem>
      <MenuItem
        value={TEAM_MEMBER_ROLE}
        disabled={Boolean(props.memberUnavailableReason)}
        sx={{ maxWidth: 320, whiteSpace: "normal" }}
      >
        <ListItemText primary={TEAM_MEMBER_ROLE} secondary={props.memberUnavailableReason} />
      </MenuItem>
    </Select>
  );
}
